import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import type { User } from '../models/User';
import { UserRole } from '../models/User';

export function useUsers(organizationId?: string | null, includeSuperAdmins = false) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setLoading(true);

    const mapSnapshotToUsers = (snapshot: any): User[] =>
      snapshot.docs.map((doc: any) => {
        const data = doc.data();
        return {
          uid: doc.id,
          ...data,
          clientId: data.clientId || data.organizationId || '',
          organizationId: data.organizationId || data.clientId || '',
          projectAccess: data.projectAccess || data.clientAccess || {},
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as User;
      });

    const sortByEmail = (input: User[]) =>
      [...input].sort((a, b) => {
        const aEmail = (a.email || '').toLowerCase();
        const bEmail = (b.email || '').toLowerCase();
        return aEmail.localeCompare(bEmail);
      });

    const handleSnapshotError = (err: any) => {
      console.error('Error fetching users:', err);
      const errorMessage = err?.message || '';
      if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
        setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
      } else {
        setError(err as Error);
      }
      setLoading(false);
    };

    if (organizationId && includeSuperAdmins) {
      const orgQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId)
      );
      const superAdminQuery = query(
        collection(db, 'users'),
        where('role', '==', UserRole.SUPER_ADMIN)
      );

      let orgUsers: User[] = [];
      let superAdminUsers: User[] = [];
      let orgLoaded = false;
      let superAdminsLoaded = false;

      const publishMergedUsers = () => {
        if (!orgLoaded || !superAdminsLoaded) return;
        const mergedByUid = new Map<string, User>();
        [...orgUsers, ...superAdminUsers].forEach((user) => {
          mergedByUid.set(user.uid, user);
        });
        setUsers(sortByEmail(Array.from(mergedByUid.values())));
        setError(null);
        setLoading(false);
      };

      const unsubscribeOrg = onSnapshot(
        orgQuery,
        (snapshot) => {
          orgUsers = mapSnapshotToUsers(snapshot);
          orgLoaded = true;
          publishMergedUsers();
        },
        handleSnapshotError
      );

      const unsubscribeSuperAdmins = onSnapshot(
        superAdminQuery,
        (snapshot) => {
          superAdminUsers = mapSnapshotToUsers(snapshot);
          superAdminsLoaded = true;
          publishMergedUsers();
        },
        handleSnapshotError
      );

      return () => {
        unsubscribeOrg();
        unsubscribeSuperAdmins();
      };
    }

    let q;
    let needsSorting = false;
    if (organizationId) {
      q = query(
        collection(db, 'users'),
        where('organizationId', '==', organizationId)
      );
      needsSorting = true;
    } else {
      q = query(
        collection(db, 'users'),
        orderBy('email', 'asc')
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const usersData = mapSnapshotToUsers(snapshot);
        setUsers(needsSorting ? sortByEmail(usersData) : usersData);
        setLoading(false);
        setError(null);
      },
      handleSnapshotError
    );

    return () => unsubscribe();
  }, [organizationId, includeSuperAdmins]);

  const createUser = async (userData: Omit<User, 'uid' | 'createdAt' | 'updatedAt'> & { password: string; firstName: string; lastName: string }) => {
    try {
      const createOrgUser = httpsCallable(functions, 'createOrgUser');
      const resolvedRole = userData.role || UserRole.PROJECT_ADMIN;
      const isSuperAdmin = resolvedRole === UserRole.SUPER_ADMIN;
      const resolvedOrgId = userData.clientId || userData.organizationId || '';

      // Prepare data for the cloud function
      const functionData = {
        email: userData.email || '',
        password: userData.password,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        organizationId: isSuperAdmin ? '' : resolvedOrgId,
        clientId: isSuperAdmin ? '' : resolvedOrgId,
        role: resolvedRole,
        phone: userData.phoneNumber || '',
        timezone: 'America/Los_Angeles', // Default timezone
      };

      const result = await createOrgUser(functionData);
      return result.data;
    } catch (err: any) {
      console.error('Error creating user:', err);
      // Provide more user-friendly error messages
      if (err.code === 'functions/already-exists') {
        throw new Error('A user with this email already exists');
      } else if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to create users');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const updateUser = async (userId: string, updates: Partial<User>) => {
    try {
      const userRef = doc(db, 'users', userId);
      const relationUpdates: Record<string, unknown> = {};
      const resolvedClientId = updates.clientId || updates.organizationId;
      if (resolvedClientId !== undefined) {
        relationUpdates.clientId = resolvedClientId;
        relationUpdates.organizationId = resolvedClientId;
      }
      await updateDoc(userRef, {
        ...updates,
        ...relationUpdates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      await deleteUserAccount({ uid: userId });
    } catch (err: any) {
      console.error('Error deleting user:', err);
      // Provide more user-friendly error messages
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to delete users');
      } else if (err.code === 'functions/not-found') {
        throw new Error('User not found');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const updateUserClientAccess = async (
    userId: string,
    clientAccess: Record<string, { role: string }>
  ) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        clientAccess,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating user client access:', err);
      throw err;
    }
  };

  const setUserOrganizationClaims = async (
    userId: string,
    organizationId: string,
    role: string
  ) => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'setClaims',
        userId,
        organizationId,
        role,
      });
      return result.data;
    } catch (err: any) {
      console.error('Error setting user organization claims:', err);
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to set organization claims');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const updateUserRole = async (userId: string, role: string) => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'updateRole',
        userId,
        role,
      });
      return result.data;
    } catch (err: any) {
      console.error('Error updating user role:', err);
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to update user roles');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const removeUserFromOrganization = async (userId: string) => {
    try {
      const manageUser = httpsCallable(functions, 'manageUser');
      const result = await manageUser({
        action: 'removeFromOrg',
        uid: userId,
      });
      return result.data;
    } catch (err: any) {
      console.error('Error removing user from organization:', err);
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to remove users from organizations');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  const resetUserPassword = async (userId: string, newPassword: string) => {
    try {
      const resetUserPasswordFn = httpsCallable(functions, 'resetUserPassword');
      await resetUserPasswordFn({ userId, newPassword });
    } catch (err: any) {
      console.error('Error resetting user password:', err);
      if (err.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to reset passwords');
      } else if (err.code === 'functions/not-found') {
        throw new Error('User not found');
      } else if (err.message) {
        throw new Error(err.message);
      }
      throw err;
    }
  };

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    updateUserClientAccess,
    setUserOrganizationClaims,
    updateUserRole,
    removeUserFromOrganization,
    resetUserPassword,
  };
}
